import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import scraperApi from "api";
import { defaultCountryCodes } from "components/hosted-scraping/extraApiParams";
import axios from "axios";
import { TLDs } from "hooks/useTLDs";

export type SettingsFromApi = {
  availableCountryCodes: string[];
  availableTLDs: TLDs;
};

const emptyTLDs: TLDs = {
  amazon: [],
  google: [],
  ebay: [],
  redfin: [],
  walmart: []
};


let SettingsFromApiContext = createContext<SettingsFromApi>({
  availableCountryCodes: defaultCountryCodes,
  availableTLDs: emptyTLDs
});

export default function ApiSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SettingsFromApi>({
    availableCountryCodes: defaultCountryCodes,
    availableTLDs: emptyTLDs
  });
  const fetchSettings = useCallback(
    async (controller: AbortController | undefined) => {
      try {
        const [countryCodesResponse, tldsResponse] = await Promise.all([
          scraperApi.settings.availableCountryCodes({ signal: controller?.signal }),
          scraperApi.settings.availableTLDs({ signal: controller?.signal })
        ]);

        // TODO should we warn the user if the responses are empty and we're falling back to defaults?
        setSettings({
          availableCountryCodes: countryCodesResponse ?? defaultCountryCodes,
          availableTLDs: tldsResponse ?? emptyTLDs
        });
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error(err);
        }
      }
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchSettings(controller);
    return () => controller.abort();
  }, [fetchSettings]);

  return (
    <SettingsFromApiContext.Provider value={settings}>
      {children}
    </SettingsFromApiContext.Provider>
  );

};

export function useSettingsFromApi() {
  return useContext(SettingsFromApiContext);
}
